import React, { useCallback, useMemo, useRef } from "react";
import { useFormState } from "react-final-form";
import { useFieldArray } from "react-final-form-arrays";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import analytics from "fond/analytics";
import { selectCurrentAccount, useGetAccountSubscriptionsQuery } from "fond/api";
import {
  Account,
  ExtendedPermission,
  PermissionLevel,
  permissionLevelWeights,
  ResourceEntity,
  ShareSuggestion,
  SubscriptionStatus,
  User,
} from "fond/types";
import { makeUuid } from "fond/utils";
import { useAppSelector } from "fond/utils/hooks";
import { getPermissionLevel, isInherited, isNew } from "fond/utils/permissions";
import { AgGrid } from "fond/widgets";

import ActionCellRenderer from "./ActionCellRenderer";
import AddPermission from "./AddPermission";
import { getOptions, getUsernameLabel, labels } from "./helper";
import IdentityTypeCellRenderer from "./IdentityTypeCellRenderer";
import SelectCellRenderer from "./SelectCellRenderer";
import TagCellRenderer from "./TagCellRenderer";
import TypeCellRenderer from "./TypeCellRenderer";
import UsernameCellRenderer from "./UsernameCellRenderer";

type Option = string | ShareSuggestion;

export type NewPermission = Omit<ExtendedPermission, "Identity" | "Level"> & { Level: PermissionLevel } & (
    | { Identity: Pick<Account, "ID"> }
    | {
        Identity: Pick<User, "Email"> & {
          ID: string | undefined;
        };
      }
  );

interface IProps {
  /**
   * The Name of the form value.
   */
  name: string;
  /**
   * The PermissionLevel of the current logged in user
   */
  currentPermission?: PermissionLevel;
  /**
   * The Email / Username of the current logged in user
   */
  currentUsername: string;
  /**
   * The form field placeholder value
   */
  placeholder?: string;
  /**
   * Field Array push callback that allows a new user
   * to be added to the array of values.
   */
  push(fieldName: string, value: NewPermission): void;
  /**
   * The Resource the permission relates to
   */
  resource: ResourceEntity;
  /**
   * A list of suggested users and groups
   */
  suggestions?: Array<Option>;
  /**
   * The current fields value.
   */
  values: ExtendedPermission[];
}

const gridOptions: GridOptions<ExtendedPermission> = {
  animateRows: true,
  columnMenu: "new",
  sideBar: false,
  rowGroupPanelShow: "never",
  suppressScrollOnNewData: true,
  suppressContextMenu: true,
  defaultColDef: {
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: false,
    suppressMovable: true,
  },
  getRowId: ({ data }) => {
    const rowId = getUsernameLabel(data) || data.ID || makeUuid();
    return rowId;
  },
  onFilterChanged: (params) => {
    analytics.track("permissions.modal.action.list.filtered", {
      columnIds: Object.keys(params.api.getFilterModel()),
    });
  },
  onSortChanged: ({ api }) => {
    const columnsSorted = api
      .getColumns()
      ?.filter((col) => col.getSortIndex() !== null)
      .map((col) => col.getColId());

    if (columnsSorted) {
      analytics.track("permissions.modal.action.list.sorted", {
        columnIds: columnsSorted,
      });
    }
  },
};

const PermissionsField: React.FC<IProps> = ({
  name,
  currentPermission,
  currentUsername,
  placeholder = "Enter an email to share with",
  push,
  resource,
  suggestions = [],
  values,
}: IProps) => {
  const initialValues = useRef<ExtendedPermission[]>(values);
  const { errors: submitErrors } = useFormState();
  const gridRef = useRef<AgGridReact>(null);
  const {
    fields: { remove, update },
  } = useFieldArray("Users");
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(currentAccount?.ID ?? skipToken);
  const allExpired = !!subscriptions?.every((subscription) => subscription.Status === SubscriptionStatus.Expired);

  /**
   * Callback function for adding new users to the permission list
   */
  const handleOnAdd = useCallback(() => {
    if (gridRef.current) {
      gridRef.current?.api.ensureIndexVisible(gridRef.current.api.getDisplayedRowCount() - 1, "bottom");
    }
  }, []);

  /**
   * Defines the columns for the permissions grid
   *
   * Note: Several of the columns use a valueGetter to set the cell value to the entire row data
   * so that the cell renderers have access to all permission properties. We do this rather
   * than using the node value (that is also passed) as it can be stale in certain scenarios.
   */
  const columns: ColDef<ExtendedPermission>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "IdentityType",
        width: 50,
        cellRenderer: IdentityTypeCellRenderer,
        suppressSizeToFit: true,
      },
      {
        colId: "Identity",
        headerName: "People and groups",
        filter: "agTextColumnFilter",
        field: "Identity.Email",
        flex: 1,
        wrapText: false,
        autoHeight: true,
        resizable: true,
        valueGetter: (params) => ({ ...params.data }),
        cellRenderer: UsernameCellRenderer,
        cellRendererParams: {
          submitErrors,
          allExpired,
        },
        filterValueGetter: (params) => getUsernameLabel(params.data),
        comparator: (valueA, valueB, nodeA, nodeB) => {
          const levelA = getUsernameLabel(nodeA.data) || "";
          const levelB = getUsernameLabel(nodeB.data) || "";
          return levelA.localeCompare(levelB);
        },
      },
      {
        headerName: "Type",
        field: "External",
        colId: "Type",
        width: 100,
        cellRenderer: TypeCellRenderer,
        filter: "agSetColumnFilter",
        filterParams: {
          suppressMiniFilter: true,
          values: ["true", "false"],
          valueFormatter: (value: ICellRendererParams) => (value.value === "true" ? "External" : "Member"),
        },
      },
      {
        headerName: "",
        colId: "Tag",
        width: 100,
        valueGetter: (params) => {
          if (params.data) {
            if (isNew(params.data)) return "new";
            if (params.data.Remove) return "remove";
            if (params.data.Level !== initialValues.current.find((perm) => perm.ID === params.data?.ID)?.Level) return "modified";
            if (isInherited(params.data) && !params.data.Revert) return "inherited";
          }
          return null;
        },
        cellRenderer: TagCellRenderer,
      },
      {
        headerName: "Permission",
        field: "Level",
        colId: "Permission",
        width: 200,
        cellRenderer: SelectCellRenderer,
        cellRendererParams: {
          currentUsername,
          currentPermission,
        },
        comparator: (valueA: ExtendedPermission, valueB: ExtendedPermission) => {
          const levelA = permissionLevelWeights[getPermissionLevel(valueA)];
          const levelB = permissionLevelWeights[getPermissionLevel(valueB)];
          return levelA - levelB;
        },
        valueGetter: (params) => ({ ...params.data }),
        filter: "agSetColumnFilter",
        filterParams: {
          suppressMiniFilter: true,
          suppressSorting: true,
          values: ["Manager", "Contributor", "Collaborator", "Viewer", "Denied"],
        },
        filterValueGetter: (params) => {
          if (!params.data) return "";
          return labels[getPermissionLevel(params.data)];
        },
      },
      {
        headerName: "",
        colId: "Actions",
        width: 70,
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          update,
          remove,
          values,
          currentUsername,
          currentPermission,
        },
        valueGetter: (params) => ({
          ...params.data,
        }),
        sortable: false,
        type: "rightAligned",
      },
    ],
    [allExpired, currentPermission, currentUsername, remove, submitErrors, update, values]
  );

  return (
    <Box>
      <AddPermission
        name={name}
        push={push}
        placeholder={placeholder}
        suggestions={suggestions}
        resource={resource}
        values={values}
        options={currentPermission ? getOptions("", currentPermission) : []}
        onAdd={handleOnAdd}
      />
      <Box flexGrow={1} height={400} mt={2}>
        <AgGrid ref={gridRef} columnDefs={columns} rowData={values} gridOptions={gridOptions} variant="outlined" />
      </Box>
    </Box>
  );
};

export default PermissionsField;
